import { TLParentId, IndexKey } from '@tldraw/tldraw';

import { VisualChronologyNodeShape } from './components/TimelineElements/VisualChronologyNode';
import { DISTANCE_BETWEEN_NODES, DISTANCE_FROM_TIMELINE, FIRST_NODE_X, NODE_WIDTH, TIMELINE_Y } from './constants';
import { ChronologyFact } from '@/types';

export interface NodeData {
  nodeId: string;
  date: string | Date | null; // Actual date for chronological ordering
  dateText: string; // Display text for the date
  description: string;
  documentNames: string;
  docId: string;
  factId?: string;
}

// Convert ChronologyFact to NodeData
export const convertFactToNodeData = (fact: ChronologyFact): NodeData => ({
  nodeId: fact.id,
  date: fact.factDate ?? '',
  dateText: fact.factDateText ?? '',
  description: fact.description ?? '',
  documentNames: getDocumentNames(fact),
  docId: fact.fact.locations[0]?.doc?.id ?? '',
  factId: fact.factId,
});

export const getDocumentNames = (fact: ChronologyFact) => {
  if (!fact.fact.locations || fact.fact.locations.length === 0) return '';

  const uniqueDocs = fact.fact.locations.reduce((acc: string[], location) => {
    if (location.doc && !acc.includes(location.doc.file.name)) {
      acc.push(location.doc.file.name);
    }
    return acc;
  }, []);

  return uniqueDocs.length > 0 ? uniqueDocs.join(', ') : '';
};

// Calculate node position based on index
export const calculateNodePosition = (index: number, nodeHeight: number) => {
  const nodeX = FIRST_NODE_X + index * DISTANCE_BETWEEN_NODES;
  const isAbove = index % 2 === 0;
  const nodeY = isAbove ? TIMELINE_Y - DISTANCE_FROM_TIMELINE - nodeHeight : TIMELINE_Y + DISTANCE_FROM_TIMELINE;
  return { nodeX, nodeY, isAbove };
};

// Calculate node Y position based on isAbove flag and height
export const calculateNodeYPosition = (isAbove: boolean, nodeHeight: number) => {
  return isAbove ? TIMELINE_Y - DISTANCE_FROM_TIMELINE - nodeHeight : TIMELINE_Y + DISTANCE_FROM_TIMELINE;
};

// Create connecting line properties
export const createConnectingLineProps = (isAbove: boolean) => {
  const connectingLineLength = isAbove ? -DISTANCE_FROM_TIMELINE : DISTANCE_FROM_TIMELINE;
  return {
    dash: 'dashed',
    size: 's',
    color: 'black',
    spline: 'line',
    points: {
      '0': { id: '0', index: 'a0', x: 0, y: 0 },
      '1': { id: '1', index: 'a1', x: 0, y: connectingLineLength },
    },
    scale: 1,
  };
};

// Calculate timeline length based on nodes
export const calculateTimelineLength = (nodes: any[]) => {
  const rightmostX =
    nodes.length > 0 ? Math.max(...nodes.map((node: any) => node.x)) + NODE_WIDTH + 70 : FIRST_NODE_X + 70;
  return rightmostX;
};

// Create node shape properties
export const createNodeShapeProps = (nodeData: NodeData, nodeHeight: number, isAbove: boolean) => ({
  w: NODE_WIDTH,
  h: nodeHeight,
  date: nodeData.date,
  dateText: nodeData.dateText,
  description: nodeData.description,
  documentNames: nodeData.documentNames,
  factId: nodeData.factId ?? '',
  docId: nodeData.docId,
  isAbove: isAbove,
  nodeId: nodeData.nodeId,
});

// Find insertion position for new node based on chronological order
export const findInsertionPosition = (
  existingNodes: VisualChronologyNodeShape[],
  newNodeDate: string | Date | null,
) => {
  // Separate nodes with dates from those without
  const datedNodes: VisualChronologyNodeShape[] = [];
  const undatedNodes: VisualChronologyNodeShape[] = [];

  existingNodes.forEach((node: VisualChronologyNodeShape) => {
    if (node.props.date) {
      datedNodes.push(node);
    } else {
      undatedNodes.push(node);
    }
  });

  // Sort only the dated nodes chronologically
  datedNodes.sort((a, b) => {
    const dateA = a.props.date;
    const dateB = b.props.date;
    return dateA.localeCompare(dateB);
  });

  // Find the correct insertion position based on date
  let insertionIndex = existingNodes.length; // Default to end

  if (newNodeDate) {
    // Find position among dated nodes
    for (let i = 0; i < datedNodes.length; i++) {
      const existingDateStr = datedNodes[i].props.date;

      if (newNodeDate < existingDateStr) {
        // Find this dated node's position in the original existingNodes array
        insertionIndex = existingNodes.findIndex((node) => node.id === datedNodes[i].id);
        break;
      }
    }

    // If no position found among dated nodes, insert after the last dated node but before undated nodes
    if (insertionIndex === existingNodes.length && datedNodes.length > 0) {
      const lastDatedNode = datedNodes[datedNodes.length - 1];
      const lastDatedIndex = existingNodes.findIndex((node) => node.id === lastDatedNode.id);
      insertionIndex = lastDatedIndex + 1;
    }
  } else {
    // For undated nodes, add at the end
    insertionIndex = existingNodes.length;
  }

  return insertionIndex;
};

// Shift nodes after insertion point
export const shiftNodesAfterInsertion = (
  nodesToShift: VisualChronologyNodeShape[],
  insertionIndex: number,
  store: Record<string, any>,
) => {
  nodesToShift.forEach((node, index) => {
    const actualIndex = insertionIndex + index + 1;
    const { nodeX: newX, nodeY: newY, isAbove: newIsAbove } = calculateNodePosition(actualIndex, node.props.h);

    // Update node position and isAbove property
    node.x = newX;
    node.y = newY;
    node.props.isAbove = newIsAbove;

    // Update corresponding connecting line
    const nodeId = node.props.nodeId;
    const lineId = `shape:connecting-line-${nodeId}`;
    const line = store[lineId];
    if (line) {
      line.x = newX + NODE_WIDTH / 2;
      line.y = TIMELINE_Y;
      const connectingLineLength = newIsAbove ? -DISTANCE_FROM_TIMELINE : DISTANCE_FROM_TIMELINE;
      line.props.points['1'].y = connectingLineLength;
    }
  });
};

// Create in-between button properties
export const createInBetweenButtonProps = (index: number, centerX: number) => ({
  x: centerX - 50,
  y: TIMELINE_Y - 50,
  rotation: 0,
  isLocked: true,
  opacity: 1,
  meta: {},
  type: 'add-in-between-node-button',
  props: {
    w: 100,
    h: 100,
    index: index + 1,
  },
  parentId: 'page:page' as TLParentId,
  index: `a${Date.now() + 100 + index}` as IndexKey,
  typeName: 'shape',
});

// Update add new node button position
export const updateAddNewNodeButtonPosition = (allNodes: any[], store: Record<string, any>) => {
  const addNewNodeButton = store['shape:add-new-node-button'];
  if (addNewNodeButton) {
    const lastNodeX = allNodes.length > 0 ? allNodes[allNodes.length - 1].x : FIRST_NODE_X - DISTANCE_BETWEEN_NODES;
    addNewNodeButton.x = lastNodeX + NODE_WIDTH + 90;
  }
};
